import * as THREE from 'three'
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js'
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js'
import type { MaterialOverride } from '@ar-suite/core'

export interface GLBLoaderOptions {
  /** Path to self-hosted Draco decoder files (draco_decoder.wasm etc.). Omit to disable Draco. */
  dracoPath?: string
  /** Path to self-hosted Basis/KTX2 transcoder. Requires `renderer` for capability detection. */
  ktx2Path?: string
  renderer?: THREE.WebGLRenderer
}

export interface LoadedModel {
  root: THREE.Group
  gltf: GLTF
  /** Bounding box in cm after unit scaling. */
  bounds: THREE.Box3
}

/** GLTF loader with Draco / meshopt / KTX2 support and metres→centimetres scaling. */
export class GLBLoader {
  private loader = new GLTFLoader()
  private draco: DRACOLoader | null = null
  private ktx2: KTX2Loader | null = null

  constructor(options: GLBLoaderOptions = {}) {
    if (options.dracoPath) {
      this.draco = new DRACOLoader().setDecoderPath(options.dracoPath)
      this.loader.setDRACOLoader(this.draco)
    }
    if (options.ktx2Path && options.renderer) {
      this.ktx2 = new KTX2Loader().setTranscoderPath(options.ktx2Path).detectSupport(options.renderer)
      this.loader.setKTX2Loader(this.ktx2)
    }
    this.loader.setMeshoptDecoder(MeshoptDecoder)
  }

  /**
   * @param unitScale model units → cm (GLB authored in metres = 100)
   */
  async load(url: string, unitScale = 100, onProgress?: (fraction: number) => void): Promise<LoadedModel> {
    const gltf = await this.loader.loadAsync(url, (ev) => {
      if (onProgress && ev.lengthComputable) onProgress(ev.loaded / ev.total)
    })
    return this.wrap(gltf, unitScale)
  }

  async parse(data: ArrayBuffer, unitScale = 100, path = ''): Promise<LoadedModel> {
    const gltf = await new Promise<GLTF>((resolve, reject) => this.loader.parse(data, path, resolve, reject))
    return this.wrap(gltf, unitScale)
  }

  private wrap(gltf: GLTF, unitScale: number): LoadedModel {
    const root = new THREE.Group()
    root.name = 'glb-root'
    gltf.scene.scale.setScalar(unitScale)
    root.add(gltf.scene)
    root.traverse((o) => {
      const mesh = o as THREE.Mesh
      if (mesh.isMesh) {
        mesh.frustumCulled = false
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
        for (const m of mats) {
          const std = m as THREE.MeshStandardMaterial
          if (std.isMeshStandardMaterial) std.envMapIntensity = 1
        }
      }
    })
    root.updateMatrixWorld(true)
    const bounds = new THREE.Box3().setFromObject(root)
    return { root, gltf, bounds }
  }

  dispose(): void {
    this.draco?.dispose()
    this.ktx2?.dispose()
  }
}

/** Apply anchor.json variant material overrides by mesh or material name. */
export function applyMaterialOverrides(root: THREE.Object3D, overrides: Record<string, MaterialOverride>): void {
  root.traverse((o) => {
    const mesh = o as THREE.Mesh
    if (!mesh.isMesh) return
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
    for (const m of mats) {
      const ov = overrides[mesh.name] ?? overrides[m.name]
      if (!ov) continue
      const std = m as THREE.MeshStandardMaterial
      if (ov.color && 'color' in std) std.color.set(ov.color)
      if (ov.metalness !== undefined && 'metalness' in std) std.metalness = ov.metalness
      if (ov.roughness !== undefined && 'roughness' in std) std.roughness = ov.roughness
      if (ov.opacity !== undefined) {
        std.opacity = ov.opacity
        std.transparent = ov.opacity < 1
      }
      std.needsUpdate = true
    }
  })
}

/** Dispose geometries/materials/textures under a subtree. */
export function disposeObject(root: THREE.Object3D): void {
  root.traverse((o) => {
    const mesh = o as THREE.Mesh
    if (mesh.geometry) mesh.geometry.dispose()
    const mats = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : []
    for (const m of mats) {
      for (const v of Object.values(m)) if (v && (v as THREE.Texture).isTexture) (v as THREE.Texture).dispose()
      m.dispose()
    }
  })
}
